import { useContext } from "react";

import { Organization } from "@medplum/fhirtypes";

import { OrganizationsContext } from "@/contexts/organizations";
import { getActiveOrganizationId } from "@/utils/medplum";

/**
 * Resolves the practice the user is currently working in, out of the practices they belong to.
 *
 * A practitioner can belong to several practices, and `OrganizationsContext` holds all of them in
 * whatever order the FHIR search returned. The active one is the id `PracticeSwitch` writes to
 * local storage, so index order alone does not identify it. Falls back to the first practice when
 * no id is stored, matching how `OrganizationsProvider` picks a practice on first sign-in.
 *
 * The stored id is read on every render, because local storage is not reactive: `PracticeSwitch`
 * writes a new id and re-renders, and this hook has to see it. The returned value is a reference
 * into the context array, so re-reading costs no extra renders downstream.
 * @returns {Organization | undefined} The active practice, or undefined when the user belongs to none.
 */
export function useActiveOrganization(): Organization | undefined {
  const { organizations } = useContext(OrganizationsContext);
  const activeOrganizationId = getActiveOrganizationId();

  return organizations.find(({ id }) => id === activeOrganizationId) ?? organizations[0];
}
